import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Form, Input, Button, message, Checkbox } from 'antd';
import { UserOutlined, MailOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { register } from './RegisterServices';
import { useGlobal } from '../Shared/GlobalContext/GlobalContext';
import { useErrorHandler } from '../../Hooks/useErrorHandler';
import CommonWrapper from '../Shared/CommonWrapper/CommonWrapper';
import { resolveBackendMessage } from '../../Utils/apiMessages';
import Turnstile from 'react-turnstile';
import { useTheme } from '../Shared/GlobalContext/ThemeContext';
import useTurnstileScale from '../../Hooks/useTurnstileScale';
import i18n from '../../i18n';
import { mapTurnstileLanguage } from '../../Utils/turnstile';

const { Text } = Typography;

const Register = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loading, setLoading } = useGlobal();
  const { handleError } = useErrorHandler();
  const [captchaToken, setCaptchaToken] = React.useState('');
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const { isDarkMode } = useTheme();
  const turnstileRef = React.useRef(null);
  useTurnstileScale(turnstileRef, {}, [isDarkMode]);
  const captchaLang = mapTurnstileLanguage(i18n.language);

  const onFinish = useCallback(async (values) => {
    try {
      setLoading(true);
      if (!captchaToken) {
        message.error(t('please_verify_captcha'));
        return;
      }
      const { confirmPassword, privacy_policy_accepted, ...userData } = values;
      const registrationData = {
        ...userData,
        privacy_policy_accepted,
        term_of_service_accepted: privacy_policy_accepted,
        'turnstile_token': captchaToken
      };
      const payload = await register(registrationData, captchaToken);
      const text = resolveBackendMessage(t, payload);
      if (text) message.success(text);
      navigate('/login');
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  }, [navigate, setLoading, handleError, captchaToken, t]);

  const handleLoginRedirect = () => {
    navigate('/login');
  };

  return (
    <CommonWrapper forceOverflowScroll>
      <Form
        name="register"
        style={{ maxWidth: '100%', margin: '0 auto' }}
        onFinish={onFinish}
      >
        <Form.Item
          name="first_name"
          rules={[{ required: true, message: t('please_input_your_first_name') }]}
        >
          <Input
            prefix={<UserOutlined />}
            placeholder={t('first_name')}
            size="large"
            style={{ borderRadius: '5px' }}
          />
        </Form.Item>

        {/* Turnstile moved to end before submit */}

        <Form.Item
          name="last_name"
          rules={[{ required: true, message: t('please_input_your_last_name') }]}
        >
          <Input
            prefix={<UserOutlined />}
            placeholder={t('last_name')}
            size="large"
            style={{ borderRadius: '5px' }}
          />
        </Form.Item>

        <Form.Item
          name="email"
          rules={[
            { required: true, message: t('please_input_your_email') },
            { type: 'email', message: t('please_enter_a_valid_email') },
          ]}
        >
          <Input
            prefix={<MailOutlined />}
            placeholder={t('email_address')}
            size="large"
            style={{ borderRadius: '5px' }}
          />
        </Form.Item>

        <Form.Item
          name="password"
          rules={[
            { required: true, message: t('please_input_your_password') },
            { min: 6, message: t('password_must_be_at_least_6_characters_long') },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder={t('password')}
            size="large"
            style={{ borderRadius: '5px' }}
          />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          dependencies={['password']}
          rules={[
            { required: true, message: t('please_confirm_your_password') },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t('the_two_passwords_do_not_match')));
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder={t('confirm_password')}
            size="large"
            style={{ borderRadius: '5px' }}
          />
        </Form.Item>

        {siteKey && (
          <Turnstile
            key={`turnstile-${isDarkMode ? 'dark' : 'light'}`}
            sitekey={siteKey}
            appearance="always"
            theme={isDarkMode ? 'dark' : 'light'}
            language={captchaLang}
            autoResetOnExpire
            className="turnstile-embed"
            style={{ width: '100%' }}
            userRef={turnstileRef}
            responseField={false}
            onVerify={(token) => setCaptchaToken(token)}
            onExpire={() => setCaptchaToken('')}
            onError={() => setCaptchaToken('')}
          />
        )}

        <Form.Item
          name="privacy_policy_accepted"
          valuePropName="checked"
          style={{ marginBottom: 0, marginTop: 24 }}
          rules={[{
            required: true,
            message: t('you_must_accept_the_privacy_policy_and_terms'),
            validator: (_, value) =>
              value ? Promise.resolve() : Promise.reject(new Error(t('you_must_accept_the_privacy_policy_and_terms')))
          }]}
        >
          <Checkbox>
            <Text strong>
              {t('i_agree_to_the')}{' '}
              <a
                href="/privacy-policy"
                target="_blank"
                style={{ color: '#1890ff' }}
              >
                {t('privacy_policy')}
              </a>
              {' '}{t('and')}{' '}
              <a
                href="/terms-of-service"
                target="_blank"
                style={{ color: '#1890ff' }}
              >
                {t('terms_of_service')}
              </a>
            </Text>
          </Checkbox>
        </Form.Item>

        <Form.Item style={{ marginTop: 24 }}>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            loading={loading}
            style={{ width: '100%', borderRadius: '5px' }}
          >
            {t('register')}
          </Button>
        </Form.Item>

        <div style={{ textAlign: 'center', marginTop: '10px' }}>
          <Text>{t('already_have_an_account')} </Text>
          <Button
            type="link"
            style={{ fontSize: '0.9em', padding: 0 }}
            onClick={handleLoginRedirect}
          >
            {t('log_in')}
          </Button>
        </div>
      </Form>
    </CommonWrapper>
  );
};

export default Register;
